/**
 * スライダー共通ヘルパー関数
 * プログレスバー付きスライダー、基本スライダーの共通処理
 */

import Splide from "@splidejs/splide";
import type { Options } from "@splidejs/splide";
import { breakpoint } from "../constant/breakpoint";

/**
 * プログレスバーアニメーションを開始
 */
const startProgressAnimation = (
  activePage: HTMLElement,
  duration: number,
  onComplete?: () => void,
  startWidth = 0,
): number | null => {
  if (!activePage) return null;

  // 開始位置を設定（リフローなし）
  activePage.style.setProperty("--progress-width", `${startWidth}%`);
  activePage.style.setProperty("--progress-duration", "0ms");
  void activePage.offsetWidth; // リフローを強制

  // アニメーションを開始
  activePage.style.setProperty("--progress-duration", `${duration}ms`);
  activePage.style.setProperty("--progress-width", "100%");

  // アニメーション完了時にコールバックを実行
  if (onComplete) {
    return window.setTimeout(() => {
      onComplete();
    }, duration);
  }
  return null;
};

/**
 * プログレスバーアニメーションを停止（リセット）
 */
const stopProgressAnimation = (activePage: HTMLElement): void => {
  if (!activePage) return;
  activePage.style.setProperty("--progress-width", "0%");
  activePage.style.setProperty("--progress-duration", "0ms");
};

/**
 * プログレスバー付きスライダーを初期化
 * @param selector - スライダー要素のセレクタ
 * @param paginationSelector - ページネーション要素のセレクタ
 * @param options - Splideオプション
 * @returns 初期化したSplideインスタンスの配列
 */
export function initProgressSlider(
  selector: string,
  paginationSelector: string,
  options: Options,
): Splide[] {
  const sliders = document.querySelectorAll<HTMLElement>(selector);
  const splideInstances: Splide[] = [];

  if (sliders.length === 0) return splideInstances;

  for (const slider of Array.from(sliders)) {
    try {
      // data属性から設定を取得
      const interval = Number.parseInt(slider.dataset.interval || "5000", 10);

      const splide = new Splide(slider, options);
      splide.mount();
      splideInstances.push(splide);

      const pagination = slider.querySelector(
        paginationSelector,
      ) as HTMLElement;
      if (!pagination) continue;

      // スライドが1枚のときSplideはpaginationを生成しないか非表示にするため手動で対応
      const realSlideCount = slider.querySelectorAll(
        ".splide__slide:not(.splide__slide--clone)",
      ).length;
      if (realSlideCount === 1) {
        // paginationアイテムがなければ手動で生成
        if (
          pagination.querySelectorAll(".splide__pagination__page").length === 0
        ) {
          const li = document.createElement("li");
          const btn = document.createElement("button");
          btn.className = "splide__pagination__page";
          btn.setAttribute("type", "button");
          li.appendChild(btn);
          pagination.appendChild(li);
        }
        // Splideがinlineでdisplayをnoneにしていても強制的に表示する
        pagination.style.display = "flex";
      }

      let progressTimer: number | null = null;
      // 1枚スライド時にmovedが発火しない場合のフォールバックタイマー
      let singleSlideFallbackTimer: number | null = null;
      let currentProgressIndex = -1;
      let isManualNavigation = false;
      let isHovered = false;
      let pausedTime = 0; // 一時停止した時の経過時間
      let progressStartTime = 0; // プログレスバー開始時刻

      // タイマーをクリアしてリセット
      const clearProgress = () => {
        if (progressTimer) {
          clearTimeout(progressTimer);
          progressTimer = null;
        }
        if (singleSlideFallbackTimer) {
          clearTimeout(singleSlideFallbackTimer);
          singleSlideFallbackTimer = null;
        }
        currentProgressIndex = -1;
        pausedTime = 0;
        const pages = pagination.querySelectorAll(
          ".splide__pagination__page",
        ) as NodeListOf<HTMLElement>;
        for (const page of Array.from(pages)) {
          stopProgressAnimation(page);
        }
      };

      // アクティブなページネーションを更新してプログレスバーを開始
      const startProgress = (index: number, resumeFromPause = false) => {
        // 既存のタイマーをクリア
        if (progressTimer) {
          clearTimeout(progressTimer);
          progressTimer = null;
        }

        const pages = pagination.querySelectorAll(
          ".splide__pagination__page",
        ) as NodeListOf<HTMLElement>;

        // ホバー解除からの再開でない場合のみリセット
        if (!resumeFromPause) {
          for (const page of Array.from(pages)) {
            page.classList.remove("is-active");
            stopProgressAnimation(page);
          }
        }

        const activePage = pages[index] as HTMLElement;
        if (!activePage) return;

        activePage.classList.add("is-active");

        // 手動ナビゲーションフラグをリセット
        const wasManual = isManualNavigation;
        isManualNavigation = false;

        // 自動再生が有効で、手動ナビゲーションでない場合のみプログレスバーを開始
        if (!wasManual) {
          const savedIndex = index;
          currentProgressIndex = index;

          // ホバー中から再開する場合は残り時間と開始位置を計算
          let duration = interval;
          let startWidth = 0;

          if (resumeFromPause && pausedTime > 0) {
            duration = interval - pausedTime;
            startWidth = (pausedTime / interval) * 100; // 経過時間から現在の幅を計算
            // 開始時刻を調整（既に経過した時間を考慮）
            progressStartTime = Date.now() - pausedTime;
          } else {
            progressStartTime = Date.now();
          }

          progressTimer = startProgressAnimation(
            activePage,
            duration,
            () => {
              // タイマーがクリアされていない、かつ同じインデックスにいる場合のみ処理を実行
              // 1枚スライドの場合はsplide.indexが期待通りに更新されないケースがあるためバイパス
              if (
                progressTimer !== null &&
                currentProgressIndex === savedIndex &&
                (realSlideCount === 1 ||
                  currentProgressIndex === splide.index) &&
                !isManualNavigation
              ) {
                const totalPages = pages.length;
                const nextIndex = (savedIndex + 1) % totalPages;

                // 現在のアクティブなページネーションを非アクティブに戻す
                const currentPage = pages[savedIndex] as HTMLElement;
                if (currentPage) {
                  currentPage.classList.remove("is-active");
                  stopProgressAnimation(currentPage);
                }

                // 次のページネーションをアクティブにする
                const nextPage = pages[nextIndex] as HTMLElement;
                if (nextPage) {
                  nextPage.classList.add("is-active");
                }

                // タイマーをクリア
                const timerId = progressTimer;
                progressTimer = null;

                // 少し遅延してからスライドを移動
                setTimeout(() => {
                  if (
                    timerId !== null &&
                    currentProgressIndex === savedIndex &&
                    (realSlideCount === 1 ||
                      currentProgressIndex === splide.index) &&
                    !isManualNavigation
                  ) {
                    // 1枚スライドの場合はSplideのController内部APIでallowSameIndex=trueを渡す
                    // （ループ後にdest===currと判定されアニメーションがスキップされるのを防ぐ）
                    const controller = (
                      splide as {
                        Components?: {
                          Controller?: {
                            go: (
                              ctrl: string,
                              allowSameIndex?: boolean,
                            ) => void;
                          };
                        };
                      }
                    ).Components?.Controller;
                    if (controller && realSlideCount === 1) {
                      controller.go("+", true);
                    } else {
                      splide.go("+");
                    }

                    // 1枚スライドの場合、movedが発火しないことがあるためフォールバック
                    if (realSlideCount === 1) {
                      const fallbackDelay =
                        ((options.speed as number) || 800) + 300;
                      singleSlideFallbackTimer = window.setTimeout(() => {
                        singleSlideFallbackTimer = null;
                        if (!isManualNavigation && !isHovered) {
                          pausedTime = 0;
                          startProgress(0);
                        }
                      }, fallbackDelay);
                    }
                  }
                }, 50);
              } else {
                progressTimer = null;
              }
            },
            startWidth,
          );
        }
      };

      // 初期状態でプログレスバーを開始
      setTimeout(() => {
        pausedTime = 0;
        startProgress(splide.index);
      }, 100);

      // スライド変更時にプログレスバーを開始
      splide.on("moved", (newIndex) => {
        // 1枚スライドのフォールバックタイマーをキャンセル（movedが正常発火した）
        if (singleSlideFallbackTimer) {
          clearTimeout(singleSlideFallbackTimer);
          singleSlideFallbackTimer = null;
        }
        setTimeout(() => {
          isManualNavigation = false;
          pausedTime = 0; // スライド移動時にpausedTimeをリセット
          startProgress(newIndex);
        }, 10);
      });

      // ページネーションクリック時
      pagination.addEventListener("click", () => {
        isManualNavigation = true;
        clearProgress();
      });

      // アローボタンクリック時
      const arrows = slider.querySelectorAll(
        ".splide__arrow",
      ) as NodeListOf<HTMLElement>;
      for (const arrow of Array.from(arrows)) {
        arrow.addEventListener("click", () => {
          isManualNavigation = true;
          clearProgress();
        });
      }

      // ホバー時に自動再生を一時停止
      slider.addEventListener("mouseenter", () => {
        isHovered = true;
        // 1枚スライドのフォールバックタイマーをキャンセル
        if (singleSlideFallbackTimer) {
          clearTimeout(singleSlideFallbackTimer);
          singleSlideFallbackTimer = null;
        }
        // 経過時間を記録
        if (progressTimer && progressStartTime > 0) {
          pausedTime = Date.now() - progressStartTime;
          clearTimeout(progressTimer);
          progressTimer = null;

          // CSS transitionを停止（現在の位置で固定）
          const pages = pagination.querySelectorAll(
            ".splide__pagination__page",
          ) as NodeListOf<HTMLElement>;
          const activePage = pages[splide.index] as HTMLElement;
          if (activePage) {
            // 現在の進行度を計算
            const currentProgress = (pausedTime / interval) * 100;
            // transitionを無効化して現在の位置に固定
            activePage.style.setProperty("--progress-duration", "0ms");
            activePage.style.setProperty(
              "--progress-width",
              `${currentProgress}%`,
            );
          }
        }
      });

      // ホバー解除時に自動再生を再開
      slider.addEventListener("mouseleave", () => {
        if (!isManualNavigation && isHovered) {
          isHovered = false;
          // 途中から再開（第2引数でフラグを渡す）
          startProgress(splide.index, true);
          // pausedTimeはスライド移動時にリセットされるので、ここではリセットしない
        } else {
          isHovered = false;
        }
      });
    } catch (error) {
      console.error(
        `Failed to initialize progress slider (${selector}):`,
        error,
      );
    }
  }

  return splideInstances;
}

/**
 * 基本的な商品スライダーを初期化
 * @param selector - スライダー要素のセレクタ
 * @param options - Splideオプション
 */
export function initProductSlider(selector: string, options?: Options): void {
  const sliders = document.querySelectorAll<HTMLElement>(selector);

  if (sliders.length === 0) return;

  const defaultOptions: Options = {
    type: "slide",
    perPage: 5,
    perMove: 1,
    gap: "16px",
    pagination: false,
    arrows: true,
    breakpoints: {
      [Number.parseInt(breakpoint.md) - 1]: {
        perPage: 2,
        gap: "8px",
        padding: { left: "16px", right: "16px" },
        fixedWidth: "40%",
      },
    },
  };

  for (const slider of Array.from(sliders)) {
    // 修正：.splide__track が存在しない要素はスキップ（custom_liquid など不正構造の要素対策）
    if (!slider.querySelector(".splide__track")) continue;

    try {
      new Splide(slider, { ...defaultOptions, ...options }).mount();
    } catch (error) {
      console.error(
        `Failed to initialize product slider (${selector}):`,
        error,
      );
    }
  }
}
